import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

// Diâmetros do DS: small = 40px (--spacing-scale-5x). medium (100px) e large
// (160px) NÃO possuem token semântico no @govbr-ds/core — são literais definidos
// no próprio componente do DS; transcritos aqui verbatim (fidelidade ao DS).
//
// Cada tamanho também fixa `--avatar-icon-size` (os --avatar-icon-* do DS), que o
// fallback usa como `font-size` do ícone — é assim que o Font Awesome do gov.br
// consome `--icon-size`.
//
// `type` espelha a prop homônima do UIKIT (nó 75:1452): `icon` (padrão) usa a
// dupla --blue-10/--blue-warm-20 do `.br-avatar .content`; `letter` usa
// --violet-warm-vivid-70 com texto --pure-0, como o UIKIT desenha as iniciais —
// o core.css pinta as duas de azul, o que deixaria as iniciais em ~1.2:1 de
// contraste. O tipo `photo` do UIKIT não precisa de par próprio: a imagem cobre
// o círculo inteiro.
const avatarVariants = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center overflow-hidden',
    'rounded-full align-middle uppercase',
    '[font-weight:var(--font-weight-bold)]',
  ].join(' '),
  {
    variants: {
      size: {
        small: [
          'size-[var(--spacing-scale-5x)]',
          '[font-size:var(--font-size-scale-up-03)]',
          '[--avatar-icon-size:var(--icon-size-2x)]',
        ].join(' '),
        medium: [
          'size-[100px]',
          '[font-size:var(--font-size-scale-up-07)]',
          '[--avatar-icon-size:var(--icon-size-5x)]',
        ].join(' '),
        large: [
          'size-[160px]',
          '[font-size:var(--font-size-scale-up-11)]',
          '[--avatar-icon-size:var(--icon-size-8x)]',
        ].join(' '),
      },
      type: {
        icon: 'bg-[var(--blue-10)] text-[var(--blue-warm-20)]',
        letter: 'bg-[var(--violet-warm-vivid-70)] text-[var(--pure-0)]',
      },
    },
    defaultVariants: {
      // O DS trata "small" como o tamanho padrão do avatar.
      size: 'small',
      // O UIKIT abre o componente em type=icon.
      type: 'icon',
    },
  }
)

function Avatar({
  className,
  size,
  type,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & VariantProps<typeof avatarVariants>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(avatarVariants({ size, type, className }))}
      {...props}
    />
  )
}

function AvatarImage({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('aspect-square size-full object-cover', className)}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      // Herda cor de fundo, texto e caixa-alta do Root (.content do DS).
      className={cn(
        'flex size-full items-center justify-center rounded-full',
        // Ícone (Font Awesome) opcional no fallback: o DS o dimensiona pelos
        // --avatar-icon-* (32/80/128px), servidos aqui em --avatar-icon-size, e
        // o desloca 0.25em para baixo — o mesmo recorte do UIKIT.
        '[&_.svg-inline--fa]:[font-size:var(--avatar-icon-size)]',
        '[&_.svg-inline--fa]:mt-[0.25em]',
        className
      )}
      {...props}
    />
  )
}

export { Avatar, AvatarFallback, AvatarImage, avatarVariants }
